import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { fetchBundles } from '../lib/api';
import { CartItemRow, UpsellCard } from './cart/CartParts';

const Cart = ({ isOpen, onClose, cartItems, onRemoveFromCart, onUpdateQuantity, onAddToCart }) => {
  const navigate = useNavigate();
  const [bundles, setBundles] = useState([]);

  useEffect(() => {
    if (!isOpen || bundles.length) return;
    fetchBundles()
      .then(setBundles)
      .catch((err) => console.error('Failed to fetch bundles for upsell:', err));
  }, [isOpen, bundles.length]);

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const total = subtotal;

  // Upsell: if customer has Mini Favor in cart, suggest upgrading to Party Starter.
  const miniFavorInCart = cartItems.find((i) => i.id === 1);
  const partyStarter = bundles.find((b) => b.id === 2);
  const showUpsell = Boolean(miniFavorInCart && partyStarter && !cartItems.find((i) => i.id === 2));
  const upsellPrice = showUpsell
    ? (miniFavorInCart.priceType === 'printed' ? partyStarter.printedPrice : partyStarter.digitalPrice)
    : 0;

  const handleUpsell = () => {
    if (!partyStarter || !onAddToCart) return;
    onAddToCart({ ...partyStarter, priceType: miniFavorInCart.priceType, price: upsellPrice });
    // Remove the Mini Favor so the customer "upgrades" rather than stacks
    onRemoveFromCart(miniFavorInCart.id, miniFavorInCart.priceType);
    toast.success('Upgraded to The Party Starter!');
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    onClose();
    navigate('/checkout');
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg bg-warm-ivory">
        <SheetHeader>
          <SheetTitle className="text-2xl text-charcoal-ink">Shopping Cart</SheetTitle>
          <SheetDescription className="text-stone-taupe">
            {cartItems.length === 0 ? 'Your cart is empty' : `${cartItems.length} item(s) in your cart`}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-8 flex flex-col h-[calc(100vh-200px)]">
          <div className="flex-grow overflow-auto space-y-4">
            {cartItems.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-stone-taupe">Start adding items to your cart</p>
              </div>
            ) : (
              cartItems.map((item) => (
                <CartItemRow
                  key={`${item.id}-${item.priceType}`}
                  item={item}
                  onRemove={onRemoveFromCart}
                  onUpdateQuantity={onUpdateQuantity}
                />
              ))
            )}

            {showUpsell && (
              <UpsellCard
                miniFavorInCart={miniFavorInCart}
                upsellPrice={upsellPrice}
                onUpgrade={handleUpsell}
              />
            )}
          </div>

          {cartItems.length > 0 && (
            <div className="mt-6 space-y-4 border-t border-stone-taupe/20 pt-6">
              <div className="space-y-2">
                <div className="flex justify-between text-charcoal-ink">
                  <span>Subtotal</span>
                  <span className="font-medium">${subtotal.toFixed(2)}</span>
                </div>
                <Separator className="bg-stone-taupe/20" />
                <div className="flex justify-between text-charcoal-ink text-lg font-bold">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>

              <Button
                onClick={handleCheckout}
                data-testid="cart-checkout-button"
                className="w-full bg-charcoal-ink hover:bg-charcoal-ink/90 text-warm-ivory py-6 text-lg"
              >
                Proceed to Checkout
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default Cart;
